/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useEffect, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  ScanLine,
  RefreshCw,
  Maximize2,
  Clock,
  Glasses,
  AlertCircle,
  LogIn,
  Info,
} from 'lucide-react';
import useQrLogin from '../hooks/useQrLogin';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import defaultLogo from '../assets/logo.png';

const CARD_QR_SIZE = 204;
const MAX_MODAL_QR_SIZE = 500;
const SECONDS_PER_MINUTE = 60;
// Expiry colors: red / amber once this share of the code's lifetime is left, but never
// later than 10s / 20s (which is exactly the thresholds for the default 60s code).
const DANGER_SHARE = 0.1;
const WARNING_SHARE = 0.25;
const MIN_DANGER_SECONDS = 10;
const MIN_WARNING_SECONDS = 20;

/** `45s` for short-lived codes, `14:05` for codes that last longer than a minute (Pixo). */
const formatRemaining = (seconds, totalSeconds) => {
  if (totalSeconds <= SECONDS_PER_MINUTE) { return `${seconds}s`; }
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  const rest = String(seconds % SECONDS_PER_MINUTE).padStart(2, '0');
  return `${minutes}:${rest}`;
};

const QrLoginCard = () => {
  const {
    qrToken,
    secondsRemaining,
    totalSeconds,
    unavailable,
    loading,
    error,
    regenerate,
  } = useQrLogin();
  const { currentLogoUrl } = useAuth();
  const [showModal, setShowModal] = useState(false);

  // Nothing to enlarge once the QR is no longer available (e.g. Pixo login required)
  useEffect(() => {
    if (unavailable) { setShowModal(false); }
  }, [unavailable]);

  // Track window dimensions to ensure modal QR displays full size on larger displays
  // while automatically scaling down on smaller device resolutions without overflowing.
  const [windowDimensions, setWindowDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const modalQrSize = useMemo(() => {
    // Modal dialog max-height is 90vh (windowDimensions.height * 0.9).
    // Total non-QR vertical height inside modal:
    // Header (~52px), description text (~24px), QR container padding (~32px),
    // countdown bar & button (~76px), refresh hint (~26px), vertical gaps (~20px), dialog margins (~32px) = ~262px.
    const availableModalHeight = windowDimensions.height * 0.88 - 262;
    const availableModalWidth = windowDimensions.width - 120;

    // Display full size (up to 500px) on large screens, automatically reduce on smaller resolutions
    const target = Math.min(MAX_MODAL_QR_SIZE, availableModalHeight, availableModalWidth);
    return Math.max(160, Math.floor(target));
  }, [windowDimensions.height, windowDimensions.width]);

  // Use current sub-account brand logo if available, fallback to default logo
  const logoSrc = currentLogoUrl || defaultLogo;
  const refreshing = loading && !!qrToken;

  // Countdown progress over the code's full lifetime (60s default, 15 min for Pixo)
  const progressPercent = Math.min(100, Math.max(0, (secondsRemaining / totalSeconds) * 100));
  const dangerSeconds = Math.max(MIN_DANGER_SECONDS, Math.round(totalSeconds * DANGER_SHARE));
  const warningSeconds = Math.max(MIN_WARNING_SECONDS, Math.round(totalSeconds * WARNING_SHARE));
  const isDanger = secondsRemaining <= dangerSeconds;
  const isWarning = !isDanger && secondsRemaining <= warningSeconds;
  const remainingLabel = formatRemaining(secondsRemaining, totalSeconds);

  /**
   * Renders the live QR SVG with optimal module density:
   * - Balanced error correction ('Q' when logo is present for robust decoding, 'M' when no logo)
   * - Sized proportionally so finder patterns (corner 7x7 boxes & 3x3 inner eyes) remain large and sharp
   * - Crisp edge rendering for optimal optical camera capture
   */
  const renderQr = (size) => {
    const logoDimension = logoSrc ? Math.round(size * 0.18) : 0;

    return (
      <div className="relative inline-block select-none">
        <QRCodeSVG
          value={qrToken || ''}
          size={size}
          level={logoSrc ? 'Q' : 'M'}
          includeMargin={false}
          bgColor="#ffffff"
          fgColor="#0f172a"
          imageSettings={
            logoSrc
              ? {
                src: logoSrc,
                height: logoDimension,
                width: logoDimension,
                excavate: true,
              }
              : undefined
          }
          className={`transition-all duration-300 max-w-full h-auto ${refreshing ? 'blur-sm opacity-40 scale-[0.98]' : 'opacity-100 scale-100'}`}
          style={{ shapeRendering: 'crispEdges' }}
        />

        {/* Live Refreshing Spinner Overlay */}
        {refreshing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[2px] rounded-xl transition-all">
            <RefreshCw className="w-8 h-8 text-primary animate-spin" />
            <span className="text-[10px] font-black text-primary uppercase tracking-widest mt-2">
              Refreshing...
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="glass-card rounded-2xl p-4 sm:p-5 shadow-sm border border-base-content/10 flex flex-col h-full space-y-3.5">
      {/* Card Header with Status & Quick Actions */}
      <div className="flex items-center justify-between pb-2.5 border-b border-base-content/10 gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 shadow-sm ring-1 ring-primary/20">
            <ScanLine size={16} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm sm:text-base font-black text-base-content uppercase tracking-wider truncate">
                QR Instant Login
              </h2>
            </div>
            <p className="text-xs text-base-content/50 truncate">
              Scan this code with smart glasses to sign in instantly.
            </p>
          </div>
        </div>

        {/* Quick Action Toolbar */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={regenerate}
            disabled={loading}
            title="Generate new QR token"
            className="p-1.5 text-base-content/50 hover:text-primary hover:bg-primary/10 rounded-xl transition-all disabled:opacity-40"
          >
            <RefreshCw size={14} className={loading && !qrToken ? 'animate-spin text-primary' : ''} />
          </button>
          {qrToken && !error && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              title="Enlarge QR Code"
              className="p-1.5 text-base-content/50 hover:text-primary hover:bg-primary/10 rounded-xl transition-all"
            >
              <Maximize2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Main QR Display Body */}
      <div className="flex flex-col items-center justify-center flex-1 gap-3 py-1">
        {/* Loading Initial State */}
        {loading && !qrToken && (
          <div className="flex flex-col items-center justify-center h-56 w-full space-y-3 bg-base-200/40 rounded-2xl border border-base-content/5">
            <div className="w-9 h-9 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
            <span className="text-[11px] font-black uppercase tracking-widest text-base-content/50">
              Generating Secure Token...
            </span>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center h-56 w-full space-y-3 p-4 text-center bg-error/5 rounded-2xl border border-error/20">
            <div className="w-9 h-9 rounded-full bg-error/10 text-error flex items-center justify-center">
              <AlertCircle size={18} />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-bold text-error">Generation Failed</p>
              <p className="text-[11px] text-base-content/60 max-w-xs">{error}</p>
            </div>
            <button
              type="button"
              onClick={regenerate}
              className="btn btn-sm btn-primary rounded-xl gap-2 font-bold text-xs shadow-sm hover:shadow transition-all"
            >
              <RefreshCw size={13} />
              Try Again
            </button>
          </div>
        )}

        {/* Unavailable State — e.g. Pixo-enabled account without a Pixo sign-in */}
        {!loading && !error && unavailable && (
          <div className="flex flex-col items-center justify-center h-56 w-full space-y-3 p-4 text-center bg-warning/5 rounded-2xl border border-warning/20">
            <div className="w-9 h-9 rounded-full bg-warning/10 text-warning flex items-center justify-center">
              <LogIn size={18} />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-bold text-base-content">QR Login Unavailable</p>
              <p className="text-[11px] text-base-content/60 max-w-xs">{unavailable.message}</p>
            </div>
            <button
              type="button"
              onClick={regenerate}
              className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl gap-2 font-bold text-xs hover:bg-base-200"
            >
              <RefreshCw size={13} />
              Check Again
            </button>
          </div>
        )}

        {/* QR Code Container with High-Contrast Viewfinder & Finder Pattern Framing */}
        {qrToken && !error && (
          <div className="w-full flex flex-col items-center gap-3">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowModal(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowModal(true);
                }
              }}
              className="group relative p-3 sm:p-3.5 bg-white rounded-2xl border-2 border-slate-200/80 shadow-md transition-all duration-300 hover:shadow-xl hover:scale-[1.02] hover:border-primary/40 cursor-pointer"
              aria-label="Click to enlarge QR code for scanning"
            >
              {/* Futuristic Viewfinder Target Corners */}
              <div className="absolute top-2 left-2 w-3.5 h-3.5 border-t-2 border-l-2 border-primary/80 rounded-tl pointer-events-none transition-all group-hover:scale-110 group-hover:border-primary" />
              <div className="absolute top-2 right-2 w-3.5 h-3.5 border-t-2 border-r-2 border-primary/80 rounded-tr pointer-events-none transition-all group-hover:scale-110 group-hover:border-primary" />
              <div className="absolute bottom-2 left-2 w-3.5 h-3.5 border-b-2 border-l-2 border-primary/80 rounded-bl pointer-events-none transition-all group-hover:scale-110 group-hover:border-primary" />
              <div className="absolute bottom-2 right-2 w-3.5 h-3.5 border-b-2 border-r-2 border-primary/80 rounded-br pointer-events-none transition-all group-hover:scale-110 group-hover:border-primary" />

              {/* Render High-Visibility QR */}
              {renderQr(CARD_QR_SIZE)}

              {/* Hover Badge Indicator */}
              <div className="absolute inset-0 rounded-2xl bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors flex items-center justify-center pointer-events-none">
                <div className="px-3 py-1 rounded-full bg-slate-900/85 backdrop-blur-sm text-white text-[10px] font-bold shadow-lg opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 flex items-center gap-1.5">
                  <Maximize2 size={11} />
                  <span>Click to Enlarge</span>
                </div>
              </div>
            </div>

            {/* Live Expiry Countdown & Progress Tracker */}
            <div className="w-full max-w-[240px] space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-base-content/60 font-semibold text-[11px]">
                  <Clock
                    size={12}
                    className={isDanger ? 'text-error animate-pulse' : 'text-primary'}
                  />
                  <span>Security Token Expiry</span>
                </span>
                <span
                  className={`font-mono font-bold px-2 py-0.5 rounded-md text-[10px] tracking-tight ${isDanger
                    ? 'bg-error/10 text-error font-extrabold animate-pulse'
                    : isWarning
                      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                      : 'bg-primary/10 text-primary'
                    }`}
                >
                  {secondsRemaining > 0 ? remainingLabel : 'Refreshing...'}
                </span>
              </div>

              {/* Depleting Countdown Bar */}
              <div className="w-full h-1.5 bg-base-content/10 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-1000 ease-linear rounded-full ${isDanger
                    ? 'bg-error'
                    : isWarning
                      ? 'bg-amber-500'
                      : 'bg-primary'
                    }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Smart Glasses Micro-Guide Footer */}
      <div className="p-2.5 sm:p-3 rounded-xl bg-base-200/50 border border-base-content/5 space-y-1.5">
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-base-content/50">
          <Glasses size={12} className="text-primary" />
          <span>Smart Glasses Scan Steps</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-1.5 rounded-lg bg-base-100/70 border border-base-content/5 flex flex-col items-center justify-center space-y-0.5">
            <span className="w-4 h-4 rounded-full bg-primary/10 text-primary font-bold text-[9px] flex items-center justify-center">
              1
            </span>
            <span className="text-[10px] font-semibold text-base-content/70 leading-tight">
              Wear Glasses
            </span>
          </div>
          <div className="p-1.5 rounded-lg bg-base-100/70 border border-base-content/5 flex flex-col items-center justify-center space-y-0.5">
            <span className="w-4 h-4 rounded-full bg-primary/10 text-primary font-bold text-[9px] flex items-center justify-center">
              2
            </span>
            <span className="text-[10px] font-semibold text-base-content/70 leading-tight">
              Look at QR
            </span>
          </div>
          <div className="p-1.5 rounded-lg bg-base-100/70 border border-base-content/5 flex flex-col items-center justify-center space-y-0.5">
            <span className="w-4 h-4 rounded-full bg-success/10 text-success font-bold text-[9px] flex items-center justify-center">
              3
            </span>
            <span className="text-[10px] font-semibold text-base-content/70 leading-tight">
              Instant Sign In
            </span>
          </div>
        </div>

        {/* Refresh Hint */}
        <p className="flex items-start gap-1.5 text-[11px] text-base-content/60 leading-snug">
          <Info size={12} className="text-primary flex-shrink-0 mt-0.5" />
          <span>After scanning the QR code, click the Refresh button to generate a new QR code.</span>
        </p>
      </div>

      {/* Enlarged Modal for Maximum Visibility & Effortless Capture */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Smart Glasses QR Login"
        className="max-w-2xl w-full no-scrollbar"
      >
        <div className="flex flex-col items-center gap-3.5 py-1">
          <p className="text-xs sm:text-sm text-base-content/60 text-center max-w-md">
            Hold your smart glasses in front of the screen to authenticate automatically.
          </p>

          {/* High-Contrast Large QR Container */}
          {qrToken && !error && (
            <div className="relative p-3.5 sm:p-4 bg-white border-2 border-slate-200 rounded-3xl shadow-2xl inline-block max-w-full">
              {/* Modal Viewfinder Corners */}
              <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-primary rounded-tl pointer-events-none" />
              <div className="absolute top-3 right-3 w-5 h-5 border-t-2 border-r-2 border-primary rounded-tr pointer-events-none" />
              <div className="absolute bottom-3 left-3 w-5 h-5 border-b-2 border-l-2 border-primary rounded-bl pointer-events-none" />
              <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-primary rounded-br pointer-events-none" />

              {renderQr(modalQrSize)}
            </div>
          )}

          {/* Modal Countdown & Status Controls */}
          {qrToken && !error && (
            <div className="w-full max-w-md space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-base-content/60 font-semibold">
                  <Clock
                    size={13}
                    className={isDanger ? 'text-error animate-pulse' : 'text-primary'}
                  />
                  <span>Session Expiry</span>
                </span>
                <span
                  className={`font-mono font-bold px-2.5 py-0.5 rounded-full text-xs ${isDanger
                    ? 'bg-error/10 text-error animate-pulse'
                    : 'bg-primary/10 text-primary'
                    }`}
                >
                  {secondsRemaining > 0 ? `${remainingLabel} remaining` : 'Refreshing QR...'}
                </span>
              </div>

              <div className="w-full h-2 bg-base-content/10 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-1000 ease-linear rounded-full ${isDanger
                    ? 'bg-error'
                    : isWarning
                      ? 'bg-amber-500'
                      : 'bg-primary'
                    }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              {/* Refresh Hint */}
              <p className="flex items-start justify-center gap-1.5 text-xs text-base-content/60 text-center leading-snug">
                <Info size={13} className="text-primary flex-shrink-0 mt-0.5" />
                <span>After scanning the QR code, click the Refresh button to generate a new QR code.</span>
              </p>

              {/* Action Buttons */}
              <div className="flex items-center justify-center pt-1">
                <button
                  type="button"
                  onClick={regenerate}
                  disabled={loading}
                  className="btn btn-sm btn-ghost border border-base-content/10 rounded-xl gap-2 font-bold hover:bg-base-200 text-xs"
                >
                  <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                  <span>Refresh Code</span>
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center space-y-3 text-center py-4">
              <p className="text-error text-xs font-semibold">{error}</p>
              <button
                type="button"
                onClick={regenerate}
                className="btn btn-sm btn-primary rounded-xl gap-2 font-bold text-xs"
              >
                <RefreshCw size={13} />
                Regenerate Token
              </button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default QrLoginCard;
